import { dispatchAction } from '@ardoq/rxbeach';
import { IconName } from '@ardoq/icons';
import { isTruthy } from '@ardoq/common-helpers';
import {
  NameLabelDict,
  RefTypeIds,
  ViewSettingsShape,
  ViewStreamShape,
} from './types';
import {
  type SettingsConfig,
  SettingsType,
  updateViewSettings,
} from '@ardoq/view-settings';
import { type DropdownOption, DropdownOptionType } from '@ardoq/dropdown-menu';
import { ViewIds } from '@ardoq/api-types';
import {
  collapseChildrenDropdownTree,
  getTreeDepthFlags,
} from '@ardoq/dependency-map';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { t, type I18nLocale } from '@ardoq/i18n';
import { GlobalReferenceTypeId, GlobalReferenceTypes } from '@ardoq/data-model';

type IsActive = (
  viewState: ViewSettingsShape,
  settingsKey: string,
  name: string
) => boolean;

const defaultIsActive: IsActive = (
  viewState: ViewSettingsShape,
  settingsKey: string,
  name: string
) => name === viewState[settingsKey as keyof ViewSettingsShape];

const isGroupingKeyActive =
  (componentTypeIds: Set<string>) =>
  (viewState: ViewSettingsShape, settingsKey: string, name: string) => {
    if (name === '') {
      // If the last selected hidden type doesn't belong to this workspace
      // let 'None' be selected in the dropdown.
      const groupingTypeId = viewState[
        settingsKey as keyof ViewSettingsShape
      ] as any as string;
      return groupingTypeId === '' || !componentTypeIds.has(groupingTypeId);
    }
    return defaultIsActive(viewState, settingsKey, name);
  };

const getOptions = (
  viewState: ViewSettingsShape,
  list: NameLabelDict[],
  settingsKey: string,
  isActive: IsActive = defaultIsActive
): DropdownOption[] =>
  list.map(({ name, label }) => ({
    label: label,
    isActive: isActive(viewState, settingsKey, name),
    onClick: getOnClick(settingsKey, name),
    type: DropdownOptionType.OPTION,
    truncateLabel: true,
  }));

const getOnClick = (key: string, value: any) => () => {
  const settings = { [key]: value };
  const persistent = true;
  dispatchAction(
    updateViewSettings({ viewId: ViewIds.CAPABILITY_MAP, settings, persistent })
  );
};

const toggleField = (
  availableRefTypeIds: GlobalReferenceTypeId[],
  selectedRefTypeIds: GlobalReferenceTypeId[],
  refTypeId: GlobalReferenceTypeId
) => {
  switch (refTypeId) {
    case RefTypeIds.ALL:
      return selectedRefTypeIds.includes(RefTypeIds.ALL)
        ? []
        : [RefTypeIds.ALL];
    case RefTypeIds.NONE:
      return [];

    default:
      if (selectedRefTypeIds.includes(RefTypeIds.ALL)) {
        return availableRefTypeIds.filter(
          selectedRefTypeId => selectedRefTypeId !== refTypeId
        );
      }
      if (selectedRefTypeIds.includes(refTypeId)) {
        return selectedRefTypeIds.filter(
          selectedRefTypeId => selectedRefTypeId !== refTypeId
        );
      }
      return selectedRefTypeIds.concat([refTypeId]);
  }
};

const getOptionClickHandler =
  (
    viewId: ViewIds,
    availableRefTypeIds: GlobalReferenceTypeId[],
    selectedRefTypeIds: GlobalReferenceTypeId[],
    refTypeId: GlobalReferenceTypeId,
    key: string
  ) =>
  () => {
    const settings = {
      [key]: toggleField(availableRefTypeIds, selectedRefTypeIds, refTypeId),
    };
    const persistent = true;
    dispatchAction(updateViewSettings({ viewId, settings, persistent }));
  };

type ReferenceTypeDict = {
  referenceTypes: GlobalReferenceTypes;
};

const getReferenceTypeOptions = (
  {
    referenceTypes: { targetReferenceTypes, sourceReferenceTypes },
  }: ReferenceTypeDict,
  viewState: ViewSettingsShape,
  i18nLocale: I18nLocale
) => {
  const showAllOutgoing = viewState.includeOutgoingReferenceTypes.includes(
    RefTypeIds.ALL
  );
  const targetRefTypeIds = sourceReferenceTypes.map(({ id }) => id);
  const outgoingRefTypes = sourceReferenceTypes.map<DropdownOption>(
    ({ id, name }) => ({
      name: id,
      label: name,
      onClick: getOptionClickHandler(
        ViewIds.CAPABILITY_MAP,
        targetRefTypeIds,
        viewState.includeOutgoingReferenceTypes,
        id,
        'includeOutgoingReferenceTypes'
      ),
      type: DropdownOptionType.OPTION,
      isActive:
        showAllOutgoing || viewState.includeOutgoingReferenceTypes.includes(id),
      truncateLabel: true,
    })
  );
  const outgoingAllNone =
    outgoingRefTypes.length > 0
      ? [
          {
            name: RefTypeIds.ALL,
            label: t(i18nLocale, 'settingsBar:showAll', 'Show all'),
            onClick: getOptionClickHandler(
              ViewIds.CAPABILITY_MAP,
              targetRefTypeIds,
              viewState.includeOutgoingReferenceTypes,
              RefTypeIds.ALL,
              'includeOutgoingReferenceTypes'
            ),
            type: DropdownOptionType.OPTION,
            isActive: viewState.includeOutgoingReferenceTypes.includes(
              RefTypeIds.ALL
            ),
          },
          {
            name: RefTypeIds.NONE,
            label: t(i18nLocale, 'settingsBar:showNone', 'Show none'),
            onClick: getOptionClickHandler(
              ViewIds.CAPABILITY_MAP,
              targetRefTypeIds,
              viewState.includeOutgoingReferenceTypes,
              RefTypeIds.NONE,
              'includeOutgoingReferenceTypes'
            ),
            type: DropdownOptionType.OPTION,
            isActive: viewState.includeOutgoingReferenceTypes.length === 0,
          },
        ]
      : [];

  const showAllIncoming = viewState.includeIncomingReferenceTypes.includes(
    RefTypeIds.ALL
  );
  const sourceRefTypeIds = targetReferenceTypes.map(({ id }) => id);
  const incomingRefTypes = targetReferenceTypes.map<DropdownOption>(
    ({ id, name }) => ({
      name: id,
      label: name,
      onClick: getOptionClickHandler(
        ViewIds.CAPABILITY_MAP,
        sourceRefTypeIds,
        viewState.includeIncomingReferenceTypes,
        id,
        'includeIncomingReferenceTypes'
      ),
      type: DropdownOptionType.OPTION,
      isActive:
        showAllIncoming || viewState.includeIncomingReferenceTypes.includes(id),
      truncateLabel: true,
    })
  );
  const incomingAllNone =
    incomingRefTypes.length > 0
      ? [
          {
            name: RefTypeIds.ALL,
            label: t(i18nLocale, 'settingsBar:showAll', 'Show all'),
            onClick: getOptionClickHandler(
              ViewIds.CAPABILITY_MAP,
              sourceRefTypeIds,
              viewState.includeIncomingReferenceTypes,
              RefTypeIds.ALL,
              'includeIncomingReferenceTypes'
            ),
            type: DropdownOptionType.OPTION,
            isActive: viewState.includeIncomingReferenceTypes.includes(
              RefTypeIds.ALL
            ),
          },
          {
            name: RefTypeIds.NONE,
            label: t(i18nLocale, 'settingsBar:showNone', 'Show none'),
            onClick: getOptionClickHandler(
              ViewIds.CAPABILITY_MAP,
              sourceRefTypeIds,
              viewState.includeIncomingReferenceTypes,
              RefTypeIds.NONE,
              'includeIncomingReferenceTypes'
            ),
            type: DropdownOptionType.OPTION,
            isActive: viewState.includeIncomingReferenceTypes.length === 0,
          },
        ]
      : [];

  return [
    outgoingRefTypes.length > 0 && {
      label: t(
        i18nLocale,
        'settingsBar:outgoingReferenceTypes',
        'Outgoing reference types'
      ),
      type: DropdownOptionType.HEADER,
    },
    ...outgoingAllNone,
    ...outgoingRefTypes,
    incomingRefTypes.length > 0 && {
      label: t(
        i18nLocale,
        'settingsBar:incomingReferenceTypes',
        'Incoming reference types'
      ),
      type: DropdownOptionType.HEADER,
    },
    ...incomingAllNone,
    ...incomingRefTypes,
  ].filter(isTruthy);
};

export const getLeftMenu = ({
  viewModel,
  viewState,
  i18nLocale,
}: {
  viewModel: ViewStreamShape;
  viewState: ViewSettingsShape;
  i18nLocale: I18nLocale;
}): SettingsConfig[] => {
  const { canSetTreeDepth, hasCollapsedNodes } = getTreeDepthFlags({
    maxTreeDepth: viewModel.maxTreeDepth,
    treeDepth: viewState.treeDepth,
  });

  return [
    {
      id: 'capabilityField',
      label: t(
        i18nLocale,
        'tabview:capabilityMap.selectFieldToOrderBy',
        'Select field to order by'
      ),
      iconName: IconName.VIEW_COMPACT,
      options: getOptions(viewState, viewModel.listFields, 'selectedFieldName'),
      type: SettingsType.DROPDOWN,
    },
    {
      id: 'supportingValue',
      label: t(
        i18nLocale,
        'tabview:capabilityMap.selectVerticalColumnValue',
        'Select vertical column value'
      ),
      iconName: IconName.VIEW_ARRAY,
      type: SettingsType.DROPDOWN,
      options: getOptions(viewState, viewModel.listValues, 'supportingValue'),
      isDisabled: viewModel.listValues.length === 0,
    },
    {
      id: 'groupingValue',
      label: t(
        i18nLocale,
        'tabview:capabilityMap.selectHiddenComponentType',
        'Select hidden component type'
      ),
      iconName: IconName.CONTAINER,
      type: SettingsType.DROPDOWN,
      options: getOptions(
        viewState,
        [
          {
            name: '',
            label: t(i18nLocale, 'tabview:capabilityMap.none', 'None'),
          },
          ...viewModel.componentTypes.map(
            ({ componentType: { id, name } }) => ({
              name: id,
              label: name,
            })
          ),
        ],
        'groupingTypeId',
        isGroupingKeyActive(
          new Set(
            viewModel.componentTypes.map(({ componentType: { id } }) => id)
          )
        )
      ),
      isDisabled: viewModel.componentTypes.length === 0,
    },
    {
      id: 'isLayoutDirectionAnyVertical',
      label: t(
        i18nLocale,
        'tabview:capabilityMap.layoutWithoutFieldValue',
        'Layout of components without field value'
      ),
      iconName: IconName.ORIENTATION_VERTICAL,
      isActive: viewState.isLayoutDirectionAnyVertical,
      type: SettingsType.TOGGLE,
      onViewSettingsUpdate,
    },
    {
      id: 'selectReferences',
      label: t(
        i18nLocale,
        'tabview:capabilityMap.selectReferenceTypes',
        'Select reference types to be included'
      ),
      iconName: IconName.REFERENCE,
      options: getReferenceTypeOptions(viewModel, viewState, i18nLocale),
      type: SettingsType.DROPDOWN,
      isDisabled: false,
      isKeepOpen: true,
    },
    // Capability-map deliberately reuses the `tabview:dependencyMap.*` keys
    // for these labels so the two collapse-tree controls translate identically;
    // future translators should keep them in sync.
    collapseChildrenDropdownTree({
      viewId: ViewIds.CAPABILITY_MAP,
      treeDepth: viewState.treeDepth,
      maxTreeDepth: viewModel.maxTreeDepth,
      canSetTreeDepth,
      hasCollapsedNodes,
      onViewSettingsUpdate,
      labels: {
        changeDepth: t(
          i18nLocale,
          'tabview:dependencyMap.changeDepth',
          'Change depth'
        ),
        collapse: t(i18nLocale, 'tabview:dependencyMap.collapse', 'Collapse'),
        expand: t(i18nLocale, 'tabview:dependencyMap.expand', 'Expand'),
        noDepth: t(
          i18nLocale,
          'tabview:dependencyMap.noDepthToCollapseOrExpand',
          'There is no depth to collapse or expand'
        ),
      },
    }),
  ];
};
